import React, { useState } from 'react';
import type { SensorData } from './SensorDetailsPanel';
import styles from './BusSection.module.scss';

type BusTabId = 'comms' | 'power' | 'adcs' | 'gps' | 'temp' | 'thrust';

interface BusSectionProps {
  onAddSensor: () => void;
  onEditSensor: (index: number) => void;
  onPasteSensor: () => void;
  sensors: SensorData[];
  groupSatelliteCount?: number;
  sensorCoverage?: number[];
  onRemoveSensor: (index: number) => void;
  onCopySensor: (index: number | 'all') => void;
  sensorClipboard: SensorData[] | null;
  disabled?: boolean;
}

const CollapseIcon = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 120ms linear' }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CommsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <circle cx="12" cy="20" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const PowerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
    <rect x="2" y="7" width="16" height="10" rx="2" />
    <path d="M22 11v2" />
    <path d="m8 11 2 2-2 2" />
    <path d="M12 11h3" />
  </svg>
);

const AdcsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const GpsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
  </svg>
);

const ThermalIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
  </svg>
);

const PropulsionIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M12 2 4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const PasteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 4.5h8" />
    <path d="M9 2h6a2 2 0 0 1 2 2v1H7V4a2 2 0 0 1 2-2z" />
    <path d="M7 7H5.5A1.5 1.5 0 0 0 4 8.5v11A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5v-11A1.5 1.5 0 0 0 18.5 7H17" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

const RemoveIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const SyncIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 15.5-6.2" />
    <path d="M18.5 3.8V8h-4.2" />
    <path d="M21 12a9 9 0 0 1-15.5 6.2" />
    <path d="M5.5 20.2V16h4.2" />
  </svg>
);

const BUS_TABS: { id: BusTabId; label: string; Icon: React.FC; disabled: boolean }[] = [
  { id: 'comms', label: 'Comms', Icon: CommsIcon, disabled: false },
  { id: 'power', label: 'Power', Icon: PowerIcon, disabled: true },
  { id: 'adcs', label: 'ADCS', Icon: AdcsIcon, disabled: true },
  { id: 'gps', label: 'GPS', Icon: GpsIcon, disabled: true },
  { id: 'temp', label: 'Thermal', Icon: ThermalIcon, disabled: true },
  { id: 'thrust', label: 'Propulsion', Icon: PropulsionIcon, disabled: true },
];

const BusSection: React.FC<BusSectionProps> = ({
  onAddSensor,
  onEditSensor,
  onPasteSensor,
  sensors,
  groupSatelliteCount,
  sensorCoverage,
  onRemoveSensor,
  onCopySensor,
  sensorClipboard,
  disabled = false,
}) => {
  const [open, setOpen] = useState(true);
  const [activeTab] = useState<BusTabId>('comms');
  const [copyFeedback, setCopyFeedback] = useState<'individual' | 'all' | null>(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const hasClipboard = !!sensorClipboard?.length;
  const isGroupView = !!groupSatelliteCount;

  const handleCopySingle = (index: number) => {
    onCopySensor(index);
    setCopyFeedback('individual');
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  const handleCopyAll = () => {
    onCopySensor('all');
    setCopyFeedback('all');
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  const handleConfirmDelete = (index: number) => {
    onRemoveSensor(index);
    setConfirmDeleteIndex(null);
  };

  return (
    <div className={styles.subBlock}>
      <div className={styles.subHd} onClick={() => setOpen(o => !o)}>
        <span className={styles.subTitle}>Bus</span>
        <span className={styles.collapseIc}>
          <CollapseIcon open={open} />
        </span>
      </div>

      {open && (
        <div className={styles.subBody}>
          <div className={styles.subIconTabs}>
            {BUS_TABS.map(({ id, label, Icon, disabled }) => (
              <div
                key={id}
                className={`${styles.busTab} ${activeTab === id ? styles.busTabActive : ''} ${disabled ? styles.busTabDisabled : ''}`}
                title={disabled ? `${label} (coming soon)` : label}
                role={disabled ? undefined : 'button'}
              >
                <Icon />
              </div>
            ))}
          </div>

          {/* Sensors table */}
          {sensors.length > 0 && (
            <div className={styles.sensorsTable}>
              <div className={styles.tableHeader}>
                <div className={styles.tableCol1}>Name</div>
                <div className={styles.tableCol2}>FOV</div>
                <div className={styles.tableCol4}>Actions</div>
              </div>
              {sensors.map((sensor, idx) => (
                <div key={idx} className={styles.tableRow} onClick={() => onEditSensor(idx)}>
                  <div className={styles.tableCol1}>
                    <span>{sensor.name || sensor.geometry}</span>
                    {isGroupView && (
                      <span
                        className={styles.coveragePill}
                        title="Edits apply to the full group and add this sensor where missing"
                      >
                        <SyncIcon />
                        {sensorCoverage?.[idx] ?? 0}/{groupSatelliteCount}
                      </span>
                    )}
                  </div>
                  <div className={styles.tableCol2}>
                    {(sensor.geometry as string) === 'Mixed' ? (
                      'Mixed'
                    ) : sensor.geometry === 'rectangular' || sensor.geometry === 'elliptical' ? (
                      sensor.fovH === 'Mixed' || sensor.fovV === 'Mixed' ? 'Mixed' : `H: ${sensor.fovH}° V: ${sensor.fovV}°`
                    ) : (
                      sensor.fov === 'Mixed' ? 'Mixed' : `${sensor.fov}°`
                    )}
                  </div>
                  <div className={styles.tableCol4} onClick={e => e.stopPropagation()}>
                    {confirmDeleteIndex === idx ? (
                      <div className={styles.deleteConfirm}>
                        <span>Delete?</span>
                        <button className={styles.confirmYes} type="button" title="Confirm delete" onClick={() => handleConfirmDelete(idx)}>Yes</button>
                        <button className={styles.confirmNo} type="button" title="Cancel delete" onClick={() => setConfirmDeleteIndex(null)}>No</button>
                      </div>
                    ) : (
                      <div className={styles.actionBtns}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleCopySingle(idx)}
                          title="Copy sensor to clipboard"
                        >
                          <CopyIcon />
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => setConfirmDeleteIndex(idx)}
                          title="Remove sensor"
                        >
                          <RemoveIcon />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add button + Copy all + Paste */}
          <div className={styles.sensorFooter}>
            <button className={styles.addSensorBtn} onClick={onAddSensor} disabled={disabled}>
              <PlusIcon />
              Add Sensor
            </button>
            {sensors.length > 0 && (
              <button
                className={styles.copyAllBtn}
                onClick={() => handleCopyAll()}
                title="Copy all sensors to clipboard"
              >
                <CopyIcon />
                Copy All
              </button>
            )}
            <button
              className={styles.pasteSensorBtn}
              onClick={hasClipboard ? onPasteSensor : undefined}
              title={hasClipboard ? 'Paste sensors from clipboard' : 'No copied sensor'}
              disabled={!hasClipboard}
            >
              <PasteIcon />
              Paste
            </button>
          </div>

          {/* Copy feedback toast */}
          {copyFeedback && (
            <div className={`${styles.copyToast} ${styles[`toast${copyFeedback === 'individual' ? 'Individual' : 'All'}`]}`}>
              {copyFeedback === 'individual' ? 'Sensor copied to clipboard' : 'All sensors copied to clipboard'}
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default BusSection;
